import { useCallback } from 'react'
import { Text, Canvas } from 'fabric'
import { calculatePositionFromPixels } from '../../utils/text/textPosition'
import type {
    MarkingPosition,
    TextAlignment,
    FabricObjectModifiedEvent,
} from '../../types'

/**
 * Параметры для хука позиционирования текста
 */
export interface UseTextPositioningParams {
    textObjectRef: React.MutableRefObject<Text | null>
    fabricCanvasRef: React.MutableRefObject<Canvas | null>
    positionRef: React.MutableRefObject<MarkingPosition | null>
    onPositionChange?: (position: MarkingPosition) => void
}

/**
 * Хук для обработки позиционирования текста
 * Конвертирует позицию из пикселей в проценты с учетом выравнивания
 */
export function useTextPositioning(
    params: UseTextPositioningParams
): (e: FabricObjectModifiedEvent) => void {
    const { textObjectRef, fabricCanvasRef, positionRef, onPositionChange } =
        params

    const handleTextModified = useCallback(
        (e: FabricObjectModifiedEvent) => {
            // Обрабатываем только события для текстового объекта
            if (
                !textObjectRef.current ||
                !fabricCanvasRef.current ||
                !onPositionChange ||
                e.target !== textObjectRef.current
            ) {
                return
            }

            const textObj = textObjectRef.current
            const fabricCanvas = fabricCanvasRef.current

            // Получаем позицию текста в пикселях
            let pixelX = textObj.left ?? 0
            const pixelY = textObj.top ?? 0

            // Получаем размеры текста для корректировки позиции
            const textWidth = textObj.width * (textObj.scaleX ?? 1)
            const textHeight = textObj.height * (textObj.scaleY ?? 1)

            // Корректируем позицию X в зависимости от выравнивания
            // Чтобы получить позицию центра текста
            const textAlign = textObj.textAlign as TextAlignment
            if (textAlign === 'left') {
                // Если выравнивание left, left - это левый край, нужно добавить половину ширины
                pixelX = pixelX + textWidth / 2
            } else if (textAlign === 'right') {
                // Если выравнивание right, left - это правый край, нужно вычесть половину ширины
                pixelX = pixelX - textWidth / 2
            }
            // Для center left уже является центром, ничего не делаем

            // Для Y позиции: top - это верхний край, нужно добавить половину высоты для получения центра
            const centerY = pixelY + textHeight / 2

            // Получаем размеры canvas
            const canvasWidth = fabricCanvas.width
            const canvasHeight = fabricCanvas.height

            // Конвертируем позицию центра текста из пикселей в проценты
            const positionPercent = calculatePositionFromPixels(
                pixelX,
                centerY,
                canvasWidth,
                canvasHeight
            )

            // Сохраняем позицию в ref и вызываем callback
            positionRef.current = positionPercent
            onPositionChange(positionPercent)
        },
        [textObjectRef, fabricCanvasRef, positionRef, onPositionChange]
    )

    return handleTextModified
}
